/**
 * Facilitator Dashboard Process Handlers
 *
 * Fetches raw data from APIs - no calculations or transformations
 * Dashboard-specific logic is in Dashboard.helpers.ts
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../libs/Firebase'
import { ActivityWithSessions } from '../../types/GeneralTypes'
import { Sprint } from '../../../../pure-js/types/Sprint'

/**
 * Raw dashboard data structure - just the data from APIs
 */
export interface FacilitatorDashboardRawData {
  activities: ActivityWithSessions[]
  /** ACTIVE sprints only — everything the dashboard renders is scoped to active sprints. */
  sprints: Sprint[]
}

// ========================================
// FACILITATOR DASHBOARD
// ========================================

/**
 * Get facilitator dashboard raw data
 * Only fetches data from APIs - no calculations or transformations
 */
export const getFacilitatorDashboard = async (): Promise<ApiResponse<FacilitatorDashboardRawData>> => {
  return handleRequest(
    () => {
      // No mocks - return empty data structure
      return Promise.resolve({
        data: {
          activities: [],
          sprints: []
        },
        success: true
      } as ApiResponse<FacilitatorDashboardRawData>)
    },
    async () => {
      try {
        // Paged mode on purpose: offset 0 carries every ACTIVE sprint in `activeSprints`, which is
        // all the dashboard renders.
        const [activitiesResult, sprintsResult] = await Promise.all([
          httpsCallable(functions, 'getUpcomingActivities')({ limit: 20 }),
          httpsCallable(functions, 'getSprints')({ limit: 1, offset: 0 })
        ])

        const activitiesData = activitiesResult.data as { success: boolean; activities?: ActivityWithSessions[] }
        const activities = activitiesData.activities || []

        const sprintsData = sprintsResult.data as { success: boolean; activeSprints?: Sprint[] }
        const sprints = sprintsData.activeSprints || []

        return {
          data: {
            activities,
            sprints
          },
          success: true
        }
      } catch (error) {
        console.error('❌ Error fetching dashboard data:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch dashboard data'
        return {
          data: {
            activities: [],
            sprints: []
          },
          success: false,
          error: errorMessage
        }
      }
    }
  )
}
